import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI } from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_MAP = {
  waiting:         { bg:'#FEF3C7',color:'#92400E',label:'Aguardando' },
  in_progress:     { bg:'#DBEAFE',color:'#1D4ED8',label:'Em andamento' },
  waiting_payment: { bg:'#FEF3C7',color:'#B45309',label:'Ag. Pagamento' },
  done:            { bg:'#D1FAE5',color:'#065F46',label:'Finalizado' },
  delivered:       { bg:'#D1FAE5',color:'#065F46',label:'Entregue' },
  cancelled:       { bg:'#FEE2E2',color:'#991B1B',label:'Cancelado' },
};

const fmt = (v) => `R$ ${parseFloat(v||0).toFixed(2).replace('.',',')}`;

export default function OrderList() {
  const [orders,  setOrders]  = useState([]);
  const [meta,    setMeta]    = useState({});
  const [search,  setSearch]  = useState('');
  const [status,  setStatus]  = useState('');
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await ordersAPI.list({ search, status, page, limit: 20 });
      setOrders(r.data.data);
      setMeta(r.data.meta);
    } catch { toast.error('Erro ao carregar ordens.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, status, page]);

  const changeStatus = async (id, newStatus) => {
    try {
      await ordersAPI.updateStatus(id, { status: newStatus });
      toast.success('Status atualizado!');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro.'); }
  };

  return (
    <div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24 }}>
        <h1 style={{ fontSize:'1.5rem',fontWeight:700,color:'#0F172A' }}>Ordens de Serviço</h1>
        <button onClick={() => navigate('/orders/new')}
          style={{ background:'#2563EB',color:'#fff',border:'none',borderRadius:8,padding:'10px 20px',fontWeight:600,cursor:'pointer' }}>
          + Nova O.S.
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display:'flex',gap:12,marginBottom:20,flexWrap:'wrap' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar por nº, cliente ou placa..."
          style={{ padding:'8px 14px',border:'1.5px solid #E5E7EB',borderRadius:8,flex:1,minWidth:200,outline:'none' }} />
        <select value={status} onChange={e=>setStatus(e.target.value)}
          style={{ padding:'8px 14px',border:'1.5px solid #E5E7EB',borderRadius:8,outline:'none' }}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_MAP).map(([v,{label}]) => <option key={v} value={v}>{label}</option>)}
        </select>
      </div>

      {/* Kanban resumido por status */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:24 }}>
        {Object.entries(STATUS_MAP).map(([st, {bg,color,label}]) => {
          const count = orders.filter(o => o.status === st).length;
          return (
            <div key={st} onClick={() => setStatus(status===st?'':st)} style={{ background:bg,borderRadius:10,padding:'12px 16px',cursor:'pointer',border:`2px solid ${status===st?color:'transparent'}` }}>
              <div style={{ fontSize:'1.4rem',fontWeight:700,color }}>{count}</div>
              <div style={{ fontSize:'.75rem',color,fontWeight:600 }}>{label}</div>
            </div>
          );
        })}
      </div>

      {/* Lista */}
      <div style={{ background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:40,textAlign:'center',color:'#6B7280' }}>Carregando...</div>
        ) : orders.length === 0 ? (
          <div style={{ padding:40,textAlign:'center',color:'#9CA3AF' }}>Nenhuma O.S. encontrada.</div>
        ) : (
          <table style={{ width:'100%',borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#F8FAFC',borderBottom:'1px solid #E5E7EB' }}>
                {['Número','Cliente','Veículo','Serviços','Total','Status','Ações'].map(h => (
                  <th key={h} style={{ padding:'12px 16px',textAlign:'left',fontSize:'.8rem',fontWeight:600,color:'#6B7280' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const s = STATUS_MAP[o.status] || STATUS_MAP.waiting;
                return (
                  <tr key={o.id} style={{ borderBottom:'1px solid #F1F5F9' }}>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ fontWeight:700,color:'#2563EB',cursor:'pointer' }} onClick={() => navigate(`/orders/${o.id}`)}>
                        #{o.order_number}
                      </span>
                    </td>
                    <td style={{ padding:'12px 16px',fontSize:'.9rem' }}>{o.customer_name || '—'}</td>
                    <td style={{ padding:'12px 16px',fontSize:'.85rem',color:'#374151' }}>
                      <div style={{ fontWeight:600 }}>{o.plate}</div>
                      <div style={{ fontSize:'.75rem',color:'#9CA3AF' }}>{o.vehicle_model}</div>
                    </td>
                    <td style={{ padding:'12px 16px',fontSize:'.85rem',color:'#6B7280' }}>{o.employee_name || '—'}</td>
                    <td style={{ padding:'12px 16px',fontWeight:700,color:'#059669' }}>{fmt(o.total)}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ background:s.bg,color:s.color,borderRadius:6,padding:'3px 10px',fontSize:'.75rem',fontWeight:600 }}>{s.label}</span>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <select defaultValue={o.status} onChange={e => changeStatus(o.id, e.target.value)}
                        style={{ fontSize:'.75rem',padding:'4px 8px',border:'1px solid #E5E7EB',borderRadius:6,outline:'none',cursor:'pointer' }}>
                        {Object.entries(STATUS_MAP).map(([v,{label}]) => <option key={v} value={v}>{label}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {meta.total_pages > 1 && (
        <div style={{ display:'flex',justifyContent:'center',gap:8,marginTop:20 }}>
          {Array.from({length: meta.total_pages}, (_, i) => i+1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              style={{ padding:'6px 14px',border:`1.5px solid ${p===page?'#2563EB':'#E5E7EB'}`,borderRadius:6,
                background:p===page?'#2563EB':'#fff',color:p===page?'#fff':'#374151',cursor:'pointer',fontWeight:p===page?700:400 }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
